// src/cn/pages/Education/EducationPageCN.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { EducationAI, getLang, buildResources } from "../../../lib/educationApi";

export default function EducationPageCN() {
  const lang = getLang();
  const { systemPrompt } = useMemo(() => buildResources(lang), [lang]);

  // UI 텍스트
  const ui = useMemo(() => {
    // ✅ CN 고정 (번역 실수 방지)
    return {
      title: "新员工培训聊天机器人 ver.1.3",
      subtitle: "面向新员工的工作手册聊天机器人",
      faq: "常见问题：",
      inputPh: "关于工作内容，欢迎随时提问…",
      ttsTitle: "朗读脚本（语音）",
      roleTitle: "情景应对练习",
      welcome:
        "您好！我是新员工培训聊天机器人 AI 睿智萍。<br><br>您可以直接提问，也可以使用 ✨ 按钮进行情景应对练习，使用 🔊 按钮练习朗读脚本语音！",
      startRolePlay:
        "✨ <strong>情景应对练习</strong>开始。AI 将扮演患者提出较棘手的情况。请根据手册尽力回答！稍后将生成场景。",
      scenarioTitle: "<strong>患者场景：</strong><br>",
      scenarioAsk: "<br><br>您将如何应对？请输入回答。",
      ttsEmpty: "请输入要转换为语音的脚本内容。",
      error: "抱歉，发生了错误。请稍后再试。",
    };
  }, []);

  // 채팅 상태
  const [messages, setMessages] = useState(/** @type {Array<{sender:'user'|'ai', html:string}>} */ ([]));
  const [query, setQuery] = useState("");
  const [isRolePlaying, setIsRolePlaying] = useState(false);
  const [scenario, setScenario] = useState("");
  const chatRef = useRef(null);

  // 스크롤 고정
  useEffect(() => {
    const el = chatRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // 초기 웰컴 메시지
  useEffect(() => {
    const timer = setTimeout(() => {
      appendAI(ui.welcome);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function appendAI(html) {
    setMessages((prev) => [...prev, { sender: "ai", html }]);
  }
  function appendUser(text) {
    setMessages((prev) => [...prev, { sender: "user", html: escapeHtml(text) }]);
  }

  function escapeHtml(s) {
    return s.replace(/[&<>"']/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" }[m]));
  }

  // ✅ onSend: FAQ 클릭 시 인풋 거치지 않고 바로 전송 가능
  async function onSend(nextText) {
    const q = (typeof nextText === "string" ? nextText : query).trim();
    if (!q) return;

    appendUser(q);

    // 인풋에서 보낸 경우만 비우기 (FAQ 클릭은 인풋 상태 유지)
    if (typeof nextText !== "string") setQuery("");

    try {
      if (isRolePlaying) {
        // 롤플레이 답변 피드백
        const { feedback } = await EducationAI.feedback({
          scenario,
          answerFromStaff: q,
        });
        appendAI(feedback?.replace(/\n/g, "<br>") || ui.error);
        setIsRolePlaying(false);
        setScenario("");
        return;
      }

      // 일반 질의
      const { answer } = await EducationAI.ask({ query: q });
      appendAI(answer?.replace(/\n/g, "<br>") || ui.error);
    } catch (e) {
      appendAI(ui.error);
    }
  }

  async function onStartRolePlay() {
    // 안내 메시지
    appendAI(ui.startRolePlay);
    try {
      const { scenario: sc } = await EducationAI.scenario();
      const scHtml = `${ui.scenarioTitle}${(sc || "").replace(/\n/g, "<br>")}${ui.scenarioAsk}`;
      appendAI(scHtml);
      setIsRolePlaying(true);
      setScenario(sc || "");
    } catch (e) {
      appendAI(ui.error);
    }
  }

  function onSpeak() {
    const text = query.trim();
    if (!text) {
      appendAI(ui.ttsEmpty);
      return;
    }
    try {
      if ("speechSynthesis" in window) {
        const utter = new SpeechSynthesisUtterance(text);
        utter.lang = "zh-CN";
        utter.rate = 1.0;
        utter.pitch = 1.0;
        window.speechSynthesis.speak(utter);
      } else {
        appendAI(ui.error);
      }
    } catch {
      appendAI(ui.error);
    }
  }

  // 추천 질문들
  const suggestions =
    lang === "zh-CN"
      ? [process.env.REACT_APP_CN_SIKTAM_SAYAK + " 的服用方法", "请说明预约规则", "韩药药效可维持多久？", "请告诉我前台的主要工作"]
      : [process.env.REACT_APP_SIKTAM_SAYAK + " 복용법 알려줘", "예약 규칙 알려줘", "한약 약효 유지기간은?", "데스크 주요 업무 알려줘"];

  return (
    <section className="edu">
      {/* 공통 헤더 블록 */}
      <div id="content_title">
        <h2>{ui.title}</h2>
        <p>{ui.subtitle}</p>
      </div>

      <div className="content">
        <div className="chatbox" style={{ minHeight: "60vh" }}>
          <div ref={chatRef} className="chatlog">
            {messages.map((m, i) => (
              <div key={i} className={`bubble ${m.sender === "user" ? "from-user" : "from-patient"}`}>
                <div className="text" dangerouslySetInnerHTML={{ __html: m.html }} />
              </div>
            ))}
          </div>
        </div>

        {/* 推荐问题 */}
        <div className="">
          <p className="inputTitle">{ui.faq}</p>
          <div className="suglst">
            {suggestions.map((q) => (
              <button
                key={q}
                className=""
                onClick={() => {
                  // ✅ 不经过输入框，直接发送到聊天
                  onSend(q);
                }}
              >
                {q}
              </button>
            ))}
          </div>
        </div>

        {/* 输入区域 */}
        <div className="edu_question">
          <div className="">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={ui.inputPh}
              className=""
              onKeyDown={(e) => {
                if (e.key === "Enter") onSend();
              }}
            />
            <button title={ui.ttsTitle} className="speak" onClick={onSpeak}>
              🔊
            </button>
            <button title={ui.roleTitle} className="play" onClick={onStartRolePlay}>
              ✨
            </button>
            <button className="send" onClick={() => onSend()}>
              ➤
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
